// Libs
import { useField } from "formik";
import { type FC, useState } from "react";

// Utils
import type { AutocompleteCloseReason, FilterOptionsState } from "@mui/material/useAutocomplete";
import { formatHoursToSlot } from "src/utils/hourSlots";

// Components
import { Autocomplete, TextField, type TextFieldProps } from "@mui/material";
import type { AutocompleteProps } from "@mui/material/Autocomplete";
import { useTranslation } from "src/design-system/core/TranslationContext";

interface OptionType {
    inputValue?: string;
    name?: string;
    id?: number | string;
}

export interface MultipleAutocompleteProps
    extends Pick<
        AutocompleteProps<OptionType, true, true, false>,
        "options" | "defaultValue" | "onChange"
    > {
    inputProps: TextFieldProps;
    name: string;
    disabled?: boolean;
    getOptionSelected?: any;
    classes?: any;
    handleChange?: (event: any) => void;
    multiple?: boolean;
    maxItem?: number;
    disableCloseOnSelect?: boolean;
    fullWidth?: boolean;
    filterOptions?: (options: OptionType[], state: FilterOptionsState<OptionType>) => any[];
    onOpen?: (event: any) => void;
    onClose?: (event: any, r?: AutocompleteCloseReason) => void;
    freeSolo?: boolean;
    onInputBlur?: (value: any) => void;
    noOptionsText?: string;
    isDisplayHelper?: boolean;
    isOpen?: boolean;
    renderTags?: () => JSX.Element | null;
    renderOption?: (props, option: OptionType) => JSX.Element | null;
    getOptionDisabled?: (option: OptionType) => boolean;
    renderGroup?: (params: any) => JSX.Element | null;
    groupBy?: (option: OptionType) => string;
    defaultDisplayValue?: any;
    isHaveToSelfChange?: boolean;
    variant?: "standard" | "filled" | "outlined";
}

export const MultipleAutocompleteComponent: FC<MultipleAutocompleteProps> = ({
    options,
    defaultValue,
    name,
    isHaveToSelfChange = true,
    disabled,
    handleChange,
    getOptionDisabled,
    getOptionSelected,
    multiple = true,
    maxItem,
    filterOptions,
    disableCloseOnSelect,
    fullWidth,
    inputProps: { label, placeholder, type } = {},
    onOpen,
    onClose,
    freeSolo = false,
    onInputBlur,
    noOptionsText,
    isOpen,
    classes = {},
    isDisplayHelper = true,
    renderTags,
    renderOption,
    renderGroup,
    groupBy,
    defaultDisplayValue,
    variant = "outlined",
}) => {
    const [field, meta] = useField(name);
    const { translateCommon } = useTranslation();
    const [inputValue, setInputValue] = useState(undefined);
    const currentValue = multiple
        ? field.value
        : (options?.find((elm) => elm.id === field.value) ?? null);
    return (
        <Autocomplete
            freeSolo={freeSolo}
            noOptionsText={noOptionsText}
            multiple={multiple}
            defaultValue={defaultValue}
            options={options}
            renderTags={renderTags}
            renderGroup={renderGroup}
            includeInputInList
            disableClearable
            isOptionEqualToValue={getOptionSelected}
            autoComplete
            autoHighlight
            fullWidth={fullWidth}
            disabled={disabled}
            classes={classes}
            disableCloseOnSelect={disableCloseOnSelect}
            value={currentValue}
            filterOptions={filterOptions}
            getOptionLabel={(option) => option.name ?? ""}
            open={isOpen === undefined ? undefined : isOpen}
            onOpen={(...e) => {
                if (defaultDisplayValue && !field.value) {
                    field.onChange(name)({
                        target: {
                            value: defaultDisplayValue,
                        },
                    } as any);
                }

                if (onOpen) {
                    onOpen(...e);
                }
            }}
            onClose={(...e) => {
                if (onClose) {
                    onClose(...e);
                }
            }}
            renderOption={renderOption}
            groupBy={groupBy}
            onInputChange={(e, value) => {
                if (value === "" && defaultDisplayValue) {
                    field.onChange(name)({
                        ...e,
                        target: {
                            value: defaultDisplayValue,
                        },
                    });
                }
                if (!onInputBlur) {
                    return;
                }
                setInputValue(value);
            }}
            getOptionDisabled={(option) => {
                if (getOptionDisabled) {
                    return getOptionDisabled(option);
                }
                if (multiple === false) {
                    return false;
                }

                let isDisabled = true;
                field.value?.forEach((e) => {
                    if (e.id === option.id) {
                        isDisabled = false;
                    }
                });

                return field.value?.length === maxItem && isDisabled;
            }}
            onChange={(e: any, newValue) => {
                if (multiple) {
                    if (isHaveToSelfChange) {
                        field.onChange(name)({
                            ...e,
                            target: {
                                ...e.target,
                                value: newValue,
                            },
                        });
                    }
                } else if (newValue.inputValue) {
                    // il faut sortir cette logique de l'autocomplete a terme c'est vraiment pas terrible cette gestion très métier
                    // dans un composant générique le format hour to slot ne devrait pas être ici
                    const formattedHour = formatHoursToSlot(newValue.inputValue, translateCommon);
                    const correctHour =
                        newValue.inputValue?.includes("PM") && !newValue.inputValue.includes("12:")
                            ? formattedHour + 12
                            : formattedHour;
                    if (isHaveToSelfChange) {
                        field.onChange(name)({
                            ...e,
                            target: {
                                ...e.target,
                                value: correctHour,
                            },
                        });
                    } else {
                        if (handleChange) {
                            handleChange(correctHour);
                        }
                        return;
                    }
                } else {
                    if (isHaveToSelfChange) {
                        field.onChange(name)({
                            ...e,
                            target: {
                                ...e.target,
                                value: newValue.id,
                            },
                        });
                    }
                }
                if (onInputBlur) {
                    onInputBlur(inputValue);
                }

                if (handleChange) {
                    handleChange(newValue);
                }
            }}
            renderInput={(params) => (
                <>
                    <TextField
                        {...params}
                        type={type}
                        error={Boolean(meta.error && meta.touched)}
                        helperText={isDisplayHelper && meta.touched && meta.error}
                        variant={variant}
                        label={label}
                        name={name}
                        required={true}
                        onKeyDown={(event) => {
                            // Cette ligne permet de ne pas effacer les tags sélectionné dans le cas ou ceux ci ne sont pas affiché (exemple pour la selection des produits par exemple)
                            if (renderTags && renderTags() === null && event.key === "Backspace") {
                                event.stopPropagation();
                            }
                        }}
                        onBlur={(e) => {
                            if (!onInputBlur) {
                                return;
                            }
                            onInputBlur(e.target.value);
                        }}
                        inputProps={{
                            ...params.inputProps,
                            required: false,
                        }}
                        placeholder={placeholder}
                    />
                </>
            )}
        />
    );
};

// Multiple / checkbox / Controlled

// export const MultipleAutocompleteComponentTest: FC<MultipleAutocompleteProps> = ({
//     options,
//     value,
//     setValue,
//     renderOption,
//     classes,
// }: any) => {
//     return (
//         <Autocomplete
//             options={options as any[]}
//             value={value}
//             onChange={setValue}
//             id="tags-standard"
//             multiple
//             debug
//             classes={classes}
//             renderOption={renderOption}
//             getOptionSelected={(option, value) => option.id === value.id}
//             // renderOption={renderOption}
//             getOptionLabel={(option) => option.name}
//             renderInput={(params) => <TextField {...params} label="debug" margin="normal" />}
//         />
//     );
// };
